/// <amd-module name="Repos/Artifact/Discussion/ArtifactDiscussionThreadService" />
import { translateStorageKeysToDisplayNames } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import * as Resources from "Repos/Artifact/Discussion/Resources";
import { IDiscussionThreadStore } from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThreadModel } from "Repos/Discussion/DiscussionThread";
import { DiscussionThreadPermissions } from "Repos/Discussion/DiscussionThread.Types";
import { isActiveCommentStatus } from "Repos/Discussion/Utils/DiscussionStatus";
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext, IVssService, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { ArtifactDiscussionThread, IArtifactDiscussionThreadContext, IArtifactDiscussionThreadModel } from "./ArtifactDiscussionContracts";
import { propertiesToThreadContext } from "./ArtifactDiscussionUtil";
import { DiscussionThread } from "./Contracts/Discussion";
import { getDiscussionClient } from "./RestClient/DiscussionInternal";

const enum DiscussionFilterIds {
    None = "",
    AllComments = "all-comments",
    ActiveComments = "active-comments",
    HideComments = "hide-comments"
}

export const DiscussionFilterOptions: { id: string; text: string }[] = [
    { id: DiscussionFilterIds.AllComments, text: Resources.DiscussionsFilterAllComments },
    { id: DiscussionFilterIds.ActiveComments, text: Resources.DiscussionsFilterActiveComments },
    { id: DiscussionFilterIds.HideComments, text: Resources.DiscussionsFilterHideComments }
];

const defaultFilterId = DiscussionFilterIds.AllComments;
export const DefaultDiscussionFilterOption = DiscussionFilterOptions.filter(f => f.id === defaultFilterId)[0];

export interface IArtifactDiscussionThreadService extends IVssService, IDiscussionThreadStore {
    addThread(thread: IArtifactDiscussionThreadModel): void;
    applyFilter(thread: IArtifactDiscussionThreadModel[], visible?: boolean): void;
    getDiscussionThreads(): IReadonlyObservableArray<IArtifactDiscussionThreadModel>;
    getFilteredThreads(): IReadonlyObservableArray<IArtifactDiscussionThreadModel>;
    getThreadContext(): IArtifactDiscussionThreadContext;
    hasUnsavedThreads(): boolean;
    removeThread(threadId: number): void;
    setFilter(filter: DiscussionFilterIds): void;
}

export abstract class ArtifactDiscussionThreadService extends VssService implements IArtifactDiscussionThreadService {
    private discussionThreads = new ObservableArray<IArtifactDiscussionThreadModel>();
    private filter: DiscussionFilterIds;
    private filteredThreads = new ObservableArray<IArtifactDiscussionThreadModel>();
    private personaProvider: PersonaDetailsProvider;
    private threadContext: IArtifactDiscussionThreadContext;
    private user: IdentityRef;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.personaProvider = new PersonaDetailsProvider(this.pageContext);
        this.user = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user;

        this.threadContext = {
            currentUser: this.user,
            permissions: this.getPermissions(pageContext),
            personaProvider: this.personaProvider,
            artifactUri: this.getArtifactUri(pageContext)
        };

        this.filter = DiscussionFilterIds.None;
        this.fetchDiscussionThreads();
    }

    public addThread(thread: IArtifactDiscussionThreadModel): void {
        this.discussionThreads.push(thread);
    }

    public applyFilter(threads: IArtifactDiscussionThreadModel[], visible?: boolean): void {
        for (const thread of threads) {
            thread.setVisible(typeof visible === "boolean" ? visible : this.isVisible(thread, this.filter));
        }

        this.filteredThreads.value = [...threads];
    }

    public getDiscussionThreads(): IReadonlyObservableArray<IArtifactDiscussionThreadModel> {
        return this.discussionThreads;
    }

    public getFilteredThreads(): IReadonlyObservableArray<IArtifactDiscussionThreadModel> {
        return this.filteredThreads;
    }

    public getThreadContext(): IArtifactDiscussionThreadContext {
        return this.threadContext;
    }

    public removeThread(threadId: number): void {
        const deleteIndex = this.discussionThreads.value.findIndex(t => t.id === threadId);
        if (deleteIndex >= 0) {
            this.discussionThreads.splice(deleteIndex, 1);
        }
    }

    public setFilter(filter: DiscussionFilterIds): void {
        if (filter !== this.filter) {
            this.filter = filter;
            for (const thread of this.discussionThreads.value) {
                thread.setVisible(this.isVisible(thread, filter));
            }

            this.filteredThreads.value = [...this.discussionThreads.value];
        }
    }

    public hasUnsavedThreads(): boolean {
        return this.discussionThreads.value.some(t => t.isDirty().value);
    }

    protected abstract getArtifactUri(pageContext: IVssPageContext): string;
    protected abstract getPermissions(pageContext: IVssPageContext): DiscussionThreadPermissions;

    protected processThreads(threads: DiscussionThread[]): void {
        const discussionThreads: IArtifactDiscussionThreadModel[] = [];
        const threadPromises: Promise<void>[] = [];

        // Add the threads coming from the server
        for (const thread of threads.filter(t => !t.isDeleted)) {
            const artifactThread: ArtifactDiscussionThread = { ...thread, threadContext: propertiesToThreadContext(thread) };
            const addThread = () => {
                discussionThreads.push(new DiscussionThreadModel({ thread: artifactThread }));
            };

            if (useStorageKeysInMentions(this.pageContext)) {
                const translationPromises = thread.comments.map(comment =>
                    translateStorageKeysToDisplayNames(this.pageContext, comment.content).then(
                        translatedContent => (comment.content = translatedContent)
                    )
                );

                // Once all the translations are complete, update the thread
                threadPromises.push(Promise.all(translationPromises).then(addThread));
            } else {
                addThread();
            }
        }

        Promise.all(threadPromises).then(() => {
            discussionThreads.sort((t1, t2) => t1.thread.publishedDate.getTime() - t2.thread.publishedDate.getTime());
            this.discussionThreads.value = discussionThreads;

            // Set initial filter
            if (this.filter === DiscussionFilterIds.None) {
                this.setFilter(defaultFilterId);
            }
        });
    }

    private fetchDiscussionThreads(): void {
        const { pageContext } = this;
        // Fetch discussions only if the current user is a member, this is what legacy experience does.
        if (userHasClaim(pageContext, UserClaims.Member)) {
            getDiscussionClient(pageContext)
                .getThreads(this.threadContext.artifactUri)
                .then(discussionThreads => this.processThreads(discussionThreads));
        }
    }

    private isVisible(threadModel: IArtifactDiscussionThreadModel, filter: DiscussionFilterIds): boolean {
        let visible = false;
        if (filter === DiscussionFilterIds.AllComments) {
            visible = true;
        } else if (filter === DiscussionFilterIds.ActiveComments) {
            visible = isActiveCommentStatus(threadModel.thread.status as number);
        }

        return visible;
    }
}
